import React from 'react';
import PropTypes from 'prop-types';
import BucketsInput from '../BucketsInput';
import ConditionBuilder from '../ConditionBuilder';
import TimePicker from '../TimePicker';
import LinkValue from './LinkValue';
import DropdownValue from './DropdownValue';
import MultiselectValue from './MultiselectValue';
import IncludeMultiselectValue from './IncludeMultiselectValue';
import NumberInputValue from './NumberInputValue';
import StaticTextValue from './StaticTextValue';
import ComparisonValue from './ComparisonValue';
import RangeValue from './RangeValue';
import IncludeExcludeMultiselectValue from './IncludeExcludeMultiselectValue';
import SearchInputValue from './SearchInputValue';
import { MAX_CONDITIONS_PER_FIELD } from '../../fieldsConfig';
import { useEligibilityActions } from '../../../../hooks/useEligibilityEngine';

const SA_LIGHT_OPTIONS = ['GREEN', 'YELLOW', 'RED'];

const ValueSelector = ({ rule }) => {
  const { onValueChange, onAddCondition } = useEligibilityActions();
  const disabled = !rule.enabled;

  switch (rule.id) {
    case 'odometerEligibilityRule':
    case 'priceEligibilityRule':
    case 'yearEligibilityRule':
    case 'isPriceInRange':
    case 'isMMRListed':
    case 'oveYearEligiblity':
    case 'mmrEligibilityRule':
      return <RangeValue rule={rule} onValueChange={onValueChange} disabled={false} />;
    case 'saPricingPercentOfMmrRule': {
      const { minPct: min, maxPct: max } = JSON.parse(rule.value || '{}');
      return (
        <RangeValue
          rule={{ ...rule, value: JSON.stringify({ min, max }) }}
          onValueChange={(id, v) => {
            const { min: minPct, max: maxPct } = JSON.parse(v || '{}');
            onValueChange(id, JSON.stringify({ minPct, maxPct }));
          }}
          disabled={false}
        />
      );
    }
    case 'saPricingDollarOverMmrRule': {
      const { maxDelta } = JSON.parse(rule.value || '{}');
      return (
        <NumberInputValue
          rule={{ ...rule, numberValue: maxDelta }}
          onValueChange={(id, _key, val) => onValueChange(id, JSON.stringify({ maxDelta: val }))}
        />
      );
    }
    case 'saLightEligibilityRule': {
      const allowedColors = (() => {
        try { return JSON.parse(rule.value)?.allowedColors || []; } catch (e) { return []; }
      })();
      return (
        <MultiselectValue
          rule={{
            ...rule,
            options: SA_LIGHT_OPTIONS,
            value: JSON.stringify(allowedColors),
          }}
          onValueChange={(id, newValue) => {
            const selected = JSON.parse(newValue);
            onValueChange(id, JSON.stringify({ allowedColors: selected }));
          }}
        />
      );
    }
    case 'fuelTypeEligibilityRule': {
      const fuelTypes = (() => {
        try { return JSON.parse(rule.value)?.fuelTypes || []; } catch (e) { return []; }
      })();
      return (
        <MultiselectValue
          rule={{
            ...rule,
            options: fuelTypes.map(ft => ft.name),
            value: JSON.stringify(fuelTypes.filter(ft => ft.enabled).map(ft => ft.name)),
          }}
          onValueChange={(id, newValue) => {
            const selected = JSON.parse(newValue);
            onValueChange(id, JSON.stringify({ fuelTypes: fuelTypes.map(ft => ({ ...ft, enabled: selected.includes(ft.name) })) }));
          }}
        />
      );
    }
    case 'lights':
    case 'vehicleLocation':
    case 'saleType':
    case 'inventorySource':
    case 'fuelType':
      return (
        <MultiselectValue
          rule={rule}
          onValueChange={onValueChange}
          disabled={disabled}
        />
      );

    case 'link':
      return (
        <LinkValue
          valueLinkText={rule.valueLinkText}
          valueLinkUrl={rule.valueLinkUrl}
        />
      );

    case 'dropdown':
      return (
        <DropdownValue
          rule={rule}
          onValueChange={onValueChange}
          disabled={disabled}
        />
      );

    case 'multiselect':
      return (
        <MultiselectValue
          rule={rule}
          onValueChange={onValueChange}
          disabled={disabled}
        />
      );

    case 'buckets':
      return (
        <BucketsInput
          buckets={rule.buckets}
          ruleId={rule.id}
          onValueChange={onValueChange}
          disabled={disabled}
        />
      );

    case 'include_multiselect':
      return (
        <IncludeMultiselectValue
          rule={rule}
          onValueChange={onValueChange}
          disabled={disabled}
        />
      );

    case 'number_input':
      return (
        <NumberInputValue
          rule={rule}
          onValueChange={onValueChange}
          disabled={disabled}
        />
      );

    case 'traStatusEligibilityRule':
      return <StaticTextValue staticText='do not allow TRA' disabled={disabled} />;

    case 'static_text':
      return <StaticTextValue staticText={rule.staticText} />;

    case 'comparison_value': {
      // Calculate current condition count and effective max
      const effectiveMax = rule.maxConditions ?? MAX_CONDITIONS_PER_FIELD;
      const currentCount = 1; // comparison_value counts as one condition
      const canAddCondition = rule.allowAddCondition && currentCount < effectiveMax;

      return (
        <>
          <ComparisonValue
            rule={rule}
            onValueChange={onValueChange}
            disabled={disabled}
          />
          {canAddCondition && (
            <a
              href='#'
              className='add-condition-link'
              onClick={e => {
                e.preventDefault();
                onAddCondition(rule.id);
              }}
              aria-label='Add condition'
              title='Add condition'
            >
              + Add condition
            </a>
          )}
        </>
      );
    }

    case 'range':
      return (
        <RangeValue
          rule={rule}
          onValueChange={onValueChange}
          disabled={disabled}
        />
      );

    case 'include_exclude_multiselect':
      return (
        <IncludeExcludeMultiselectValue
          rule={rule}
          onValueChange={onValueChange}
          disabled={disabled}
        />
      );

    case 'condition_builder': {
      const effectiveMax = rule.maxConditions ?? MAX_CONDITIONS_PER_FIELD;
      return (
        <ConditionBuilder
          conditions={rule.conditions}
          ruleId={rule.id}
          onValueChange={onValueChange}
          disabled={disabled}
          allowAddCondition={rule.allowAddCondition}
          maxConditions={effectiveMax}
          onAddCondition={onAddCondition}
        />
      );
    }

    case 'search_input':
      return (
        <SearchInputValue
          rule={rule}
          onValueChange={onValueChange}
          disabled={disabled}
        />
      );

    case 'time_picker':
      return (
        <TimePicker
          rule={rule}
          onValueChange={onValueChange}
          disabled={disabled}
          allowAddCondition={rule.allowAddCondition}
        />
      );

    case 'empty':
      return null;

    default:
      return null;
  }
};

ValueSelector.propTypes = {
  rule: PropTypes.object.isRequired,
};

export default ValueSelector;
